import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  ShoppingCart, Package, Tag, IndianRupee, TrendingUp,
  CreditCard, Banknote, ExternalLink, ChevronDown, BarChart3, Layers
} from 'lucide-react';

const STORE_URL = 'https://exampurstore.com';

const Ecommerce = () => {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.getStoreDashboard();
      setDashboard(res.data);
    } catch {
      toast.error('Failed to connect to Exampur Store');
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.getStoreOrders(orderFilter);
      setOrders(res.data.orders || []);
    } catch { /* ignore */ }
  }, [orderFilter]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.getStoreProducts();
      setProducts(res.data.products || []);
    } catch { /* ignore */ }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.getStoreCategories();
      setCategories(res.data.categories || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchOrders(), fetchProducts(), fetchCategories()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchOrders, fetchProducts, fetchCategories]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-100 text-emerald-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      payment_pending: 'bg-amber-100 text-amber-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
        {(status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
      </span>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Connecting to Exampur Store...</div>;
  }

  return (
    <div data-testid="ecommerce-page" className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-wide">E-Commerce</h1>
          <p className="text-slate-500 text-sm">Exampur Store — Live data from exampurstore.com</p>
        </div>
        <a
          href={`${STORE_URL}/admin`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-store-admin"
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition font-medium text-sm"
        >
          <ExternalLink size={16} /> Open Store Admin
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'orders', label: 'Orders', icon: ShoppingCart },
          { id: 'products', label: 'Products', icon: Package },
          { id: 'categories', label: 'Categories', icon: Layers },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
              tab === t.id ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Dashboard */}
      {tab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Today Stats */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Today</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
                <div className="flex items-center justify-between mb-2">
                  <IndianRupee size={22} className="opacity-80" />
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Revenue</span>
                </div>
                <p className="text-3xl font-bold">₹{(dashboard.revenue_today || 0).toLocaleString()}</p>
                <p className="text-emerald-100 text-sm mt-1">{dashboard.orders_today || 0} orders</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Banknote size={22} className="opacity-80" />
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">COD</span>
                </div>
                <p className="text-3xl font-bold">{dashboard.cod_today || 0}</p>
                <p className="text-amber-100 text-sm mt-1">Cash on Delivery</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                <div className="flex items-center justify-between mb-2">
                  <CreditCard size={22} className="opacity-80" />
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Prepaid</span>
                </div>
                <p className="text-3xl font-bold">{dashboard.prepaid_today || 0}</p>
                <p className="text-blue-100 text-sm mt-1">Online Payment</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp size={22} className="opacity-80" />
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Total</span>
                </div>
                <p className="text-3xl font-bold">₹{(dashboard.total_revenue || 0).toLocaleString()}</p>
                <p className="text-purple-100 text-sm mt-1">{dashboard.total_orders || 0} total orders</p>
              </div>
            </div>
          </div>

          {/* Overall Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-400 uppercase">Total Orders</p>
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_orders || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-400 uppercase">Pending Orders</p>
              <p className="text-2xl font-bold text-amber-600">{dashboard.pending_orders || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-400 uppercase">Active Products</p>
              <p className="text-2xl font-bold text-slate-900">{dashboard.active_products || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-400 uppercase">Categories</p>
              <p className="text-2xl font-bold text-slate-900">{dashboard.active_categories || 0}</p>
            </div>
          </div>

          {/* Top Selling Products */}
          {dashboard.product_orders?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-slate-800">Top Selling Products</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {dashboard.product_orders.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-slate-300">#{i + 1}</span>
                      <div>
                        <p className="font-medium text-slate-800">{p.title}</p>
                        <p className="text-xs text-slate-400">{p.order_count} orders, {p.total_qty} qty sold</p>
                      </div>
                    </div>
                    <p className="font-bold text-emerald-600">₹{(p.total_revenue || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Orders */}
      {tab === 'orders' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {['', 'confirmed', 'payment_pending', 'shipped', 'delivered', 'cancelled'].map(s => (
              <button
                key={s}
                onClick={() => setOrderFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  orderFilter === s ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'All'}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {orders.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No orders found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Customer</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Items</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Total</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Payment</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">City</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map(order => (
                      <tr key={order.id} className="hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{order.customer_name}</p>
                          <p className="text-xs text-slate-400">{order.customer_phone}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {order.items?.map((item, i) => (
                            <div key={i}>{item.title} x{item.quantity}</div>
                          ))}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">₹{(order.total || 0).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            order.payment_method === 'cod' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                          }`}>
                            {order.payment_method === 'cod' ? 'COD' : 'Prepaid'}
                          </span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{order.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{order.shipping_address?.city || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Products */}
      {tab === 'products' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {products.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No products found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Product</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Category</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Price</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">MRP</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">Stock</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                          <p className="font-medium text-slate-800">{p.title}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{p.category_name || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">₹{p.price}</td>
                      <td className="px-4 py-3 text-right text-slate-400 line-through">₹{p.mrp}</td>
                      <td className="px-4 py-3 text-center">{p.stock ?? '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Categories */}
      {tab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.length === 0 ? (
            <div className="col-span-3 bg-white rounded-xl border p-8 text-center text-slate-400">No categories</div>
          ) : categories.map(cat => (
            <div key={cat.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">{cat.name}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${cat.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                  {cat.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-slate-500">{cat.description || 'No description'}</p>
              <p className="text-xs text-slate-400 mt-2">Slug: {cat.slug}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Ecommerce;
